import { ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  CreditCard,
  FileText,
  LogOut,
  Menu,
  X,
  ExternalLink,
  KeyRound,
  Receipt,
  BarChart3,
} from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useAppRights } from '../hooks/useAppRights';

interface AdminLayoutProps {
  children: ReactNode;
}

interface MenuChild {
  path: string;
  label: string;
  icon: React.ElementType;
}

interface MenuItem {
  path: string;
  label: string;
  icon: React.ElementType;
  external?: boolean;
  children?: MenuChild[];
}

const menuItems: MenuItem[] = [
  { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/admin/top-up', label: 'Top Up', icon: CreditCard },
  { path: '/admin/billing', label: 'Billing', icon: FileText },
  {
    path: '/admin/applications',
    label: 'Applications',
    icon: KeyRound,
    children: [
      { path: '/admin/applications/payment-report', label: 'Payment Report', icon: BarChart3 },
      { path: '/admin/applications/payment-records', label: 'Payment Records', icon: Receipt },
    ],
  },
  { path: 'https://workstation.jclxai.com/', label: 'Workstation', icon: ExternalLink, external: true },
];

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const { canCreate, rightsLoaded } = useAppRights();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // 有权限才显示 Applications 菜单，权限未加载完成时不显示，避免无权限用户看到后闪跳
  const visibleMenuItems = menuItems.filter(
    (item) => item.path !== '/admin/applications' || (rightsLoaded && canCreate)
  );

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-zinc-900 border-b border-zinc-800 flex items-center justify-between px-4 z-50">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 text-zinc-400 hover:text-white transition-colors"
        >
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
        <span className="text-white font-semibold">ANIMIND</span>
        <div className="w-8" />
      </header>

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-64 bg-zinc-900 border-r border-zinc-800 transform transition-transform duration-300 z-40 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0`}
      >
        <div className="h-16 lg:flex items-center justify-center border-b border-zinc-800 hidden">
          <span className="text-white font-bold text-xl tracking-wider">ANIMIND</span>
        </div>

        {/* User Info */}
        <div className="p-4 border-b border-zinc-800">
          <div className="text-zinc-400 text-sm">Logged in as</div>
          <div className="text-white font-medium truncate">{user?.email}</div>
          <div className="text-emerald-400 text-sm mt-1">
            Balance: ${parseFloat(user?.balance || user?.money || '0').toFixed(2)}
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-2">
          {visibleMenuItems.map((item) => {
            const Icon = item.icon;
            const isActive = !item.external && location.pathname === item.path;
            const isSectionActive =
              !item.external &&
              (item.children?.some((child) => location.pathname === child.path) ?? false);
            return (
              <div key={item.path}>
                {item.external ? (
                  <a
                    href={item.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-zinc-400 hover:text-white hover:bg-zinc-800/50"
                  >
                    <Icon size={20} />
                    <span>{item.label}</span>
                  </a>
                ) : (
                  <Link
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                      isActive
                        ? 'bg-zinc-800 text-white'
                        : isSectionActive
                          ? 'text-white'
                          : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
                    }`}
                  >
                    <Icon size={20} />
                    <span>{item.label}</span>
                  </Link>
                )}

                {item.children && item.children.length > 0 && (
                  <div className="mt-1 space-y-1">
                    {item.children.map((child) => {
                      const ChildIcon = child.icon;
                      const childActive = location.pathname === child.path;
                      return (
                        <Link
                          key={child.path}
                          to={child.path}
                          onClick={() => setSidebarOpen(false)}
                          className={`flex items-center gap-2.5 pl-11 pr-4 py-2.5 rounded-lg text-sm transition-colors ${
                            childActive
                              ? 'bg-zinc-800/70 text-white'
                              : 'text-zinc-500 hover:text-white hover:bg-zinc-800/40'
                          }`}
                        >
                          <ChildIcon size={16} />
                          <span>{child.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Logout */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-zinc-800">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 w-full rounded-lg text-zinc-400 hover:text-red-400 hover:bg-zinc-800/50 transition-colors"
          >
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        <div className="p-6 lg:p-8">{children}</div>
      </main>
    </div>
  );
};

export default AdminLayout;